import {
  Alert,
  Box,
  Button,
  Dialog,
  Snackbar,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridColDef,
  GridInitialState,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays,
  format,
  isAfter,
  parseISO,
  startOfToday,
  isValid,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  RiAddCircleLine,
  RiCheckboxCircleLine,
  RiDeleteBinLine,
  RiInformationLine,
  RiPencilLine,
} from "@remixicon/react";
import GaneshDataGrid from "../../components/DataGrid/GaneshDataGrid";
import RecurringDialog from "../../components/Schedule/Dialogs/RecurringDialog";
import { RecurringRemoveDialog } from "../../components/Schedule/Dialogs/RecurringRemoveDialog";
import { useDeliveryDialog } from "../../hooks/useDeliveryDialog";
import useGaneshApi from "../../hooks/useGaneshApi";
import { formatDateYYYYMMDD } from "utils/formatting";

import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import { Delivery_T } from "../../types/Delivery";
import {
  DeliveryItemT,
  DialogActionMessagesT,
  DialogTypesT,
  DialogTypeT,
} from "../../types/ScheduleForm";

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [
        {
          id: 1,
          field: "is_active",
          operator: "is",
          value: "true",
        },
      ],
    },
  },
  sorting: { sortModel: [{ field: "next_delivery", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: [] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
};

export function ScheduleDeliveriesPage() {
  const { apiRequest } = useGaneshApi();
  const { date_id } = useParams();
  const queryClient = useQueryClient();
  const theme = useTheme();
  document.title = `Deliveries Schedule Management`;
  // track earliest and last next delivery dates
  const [nextDate, setNextDate] = useState<string>(
    formatDateYYYYMMDD(new Date())
  );
  const [lastDate, setLastDate] = useState<string>(
    formatDateYYYYMMDD(addDays(new Date(), 30))
  );

  const { openDialogType, setOpenDialogType } = useDeliveryDialog() as {
    openDialogType: DialogTypeT;
    setOpenDialogType: (type: DialogTypeT) => void;
  };

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const parsedDate = date_id ? formatDateYYYYMMDD(new Date(date_id)) : null;
  const isDatePast = parsedDate
    ? parsedDate < formatDateYYYYMMDD(startOfToday())
    : false;

  const { data, isLoading, isFetching, error } = useQuery<Delivery_T[], Error>({
    queryKey: ["all-deliveries"],
    queryFn: async () => {
      const response = await apiRequest<Delivery_T[]>(`/v1/deliveries`);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data || [];
    },
  });

  const selectedRows = useMemo(() => {
    return data
      ?.filter((row) => row.id && rowSelectionModel.includes(row.id))
      .map((row) => ({
        ...row,
        delivery_id: row.id,
        delivery_fulfillment_id: 0,
        group_name: row.fulfillment_groups || "",
      }));
  }, [data, rowSelectionModel]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Delivery ID",
      width: 100,
      renderCell: ({ row }) => (
        <Link to={`/schedule/deliveries/${row.id}`}>{row.id}</Link>
      ),
    },
    {
      field: "delivery_name",
      headerName: "Delivery Name",
      width: 200,
      renderCell: ({ row }) => (
        <Link to={`/schedule/deliveries/${row.id}`}>{row.delivery_name}</Link>
      ),
    },
    {
      field: "customer_name",
      headerName: "Customer",
      width: 200,
      renderCell: ({ row }) => (
        <Link to={`/customer/${row.customer_id}/deliveries`}>
          {row.customer_name}
        </Link>
      ),
    },
    {
      field: "fulfillment_groups",
      headerName: "Fulfillment Group(s)",
      width: 200,
    },
    {
      field: "most_recent_delivery",
      headerName: "Most Recent Delivery",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "next_delivery",
      headerName: "Next Delivery",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "next_volume_upgrade",
      headerName: "Next Volume Upgrade",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "license_end_date",
      headerName: "License End Date",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    { field: "country_name", headerName: "Country", width: 200 },
    {
      field: "last_shipped_volume",
      headerName: "Last Shipped Volume",
      width: 150,
      type: "number",
      renderCell: renderNumberCell,
    },
    {
      field: "fulfillment_destination",
      headerName: "Fulfillment Destination",
      width: 200,
    },
    {
      field: "count_fulfillments_on_schedule",
      headerName: "Count of Scheduled Fulfillments",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "count_volume_upgrades_on_schedule",
      headerName: "Count of Scheduled Volume Upgrades",
      type: "number",
      width: 150,
      renderCell: renderNumberCell,
    },
    {
      field: "final_scheduled_delivery",
      headerName: "Final Scheduled Delivery",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "final_scheduled_volume_upgrade",
      headerName: "Final Scheduled Volume Upgrade",
      type: "date",
      width: 150,
      valueGetter: getDateValue,
      renderCell: renderDateCell,
    },
    {
      field: "is_active",
      headerName: "Is Active",
      width: 150,
      type: "boolean",
    },
  ];

  const handleRowSelectionModelChange = useCallback(
    (newRowSelectionModel: GridRowSelectionModel) => {
      setRowSelectionModel(newRowSelectionModel);

      const selectedRowsData =
        data?.filter(
          (row: Delivery_T) => row.id && newRowSelectionModel.includes(row.id)
        ) || [];

      if (selectedRowsData.length > 0) {
        const today = startOfToday();

        const nextDeliveryDates = selectedRowsData.map(
          (row) => row.next_delivery_date
        );
        const validNextDates = nextDeliveryDates.filter((d): d is string => {
          if (!d) return false;
          const parsed = new Date(d);
          return isValid(parsed) && isAfter(parsed, today);
        });

        if (validNextDates.length > 0) {
          const closestNextDate = validNextDates.reduce((min, d) =>
            new Date(d) < new Date(min) ? d : min
          );
          setNextDate(format(new Date(closestNextDate), "yyyy-MM-dd"));
        }

        const lastDeliveryDates = selectedRowsData.map(
          (row) => row.final_scheduled_delivery
        );
        const validLastDates = lastDeliveryDates.filter((d): d is string => {
          if (!d) return false;
          return isValid(new Date(d));
        });

        if (validLastDates.length > 0) {
          const furthestLastDate = validLastDates.reduce((max, d) =>
            new Date(d) > new Date(max) ? d : max
          );
          setLastDate(format(new Date(furthestLastDate), "yyyy-MM-dd"));
        }
      } else {
        // Reset dates to default when selection is cleared
        setNextDate(formatDateYYYYMMDD(new Date()));
        setLastDate(formatDateYYYYMMDD(addDays(new Date(), 30)));
      }
    },
    [data]
  );

  const handleCloseChangeDialog = () => {
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleSuccessCallback = () => {
    queryClient.invalidateQueries({
      queryKey: ["all-deliveries"],
    });

    const action = openDialogType;

    const message =
      action === DialogTypesT.ADD
        ? DialogActionMessagesT.ADD
        : action === DialogTypesT.CHANGE || action === DialogTypesT.REMOVE
        ? DialogActionMessagesT[action](rowSelectionModel.length)
        : "Success!";

    setSnackbarMessage(message);
    setSnackbarOpen(true);
    setRowSelectionModel([]);
    setOpenDialogType(DialogTypesT.NONE);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Deliveries Schedule Management
        </Typography>
        {error && <Alert severity="error">{error.message}</Alert>}
        {!error && (
          <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 1 }}>
            {isDatePast ? (
              <Alert
                severity="info"
                icon={
                  <RiInformationLine
                    size={20}
                    style={{ color: theme.palette.info.dark, marginTop: 1 }}
                  />
                }
              >
                These deliveries are in the past and cannot be modified.
              </Alert>
            ) : (
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.CHANGE)}
                    aria-label="Change Recurring"
                  >
                    <RiPencilLine size={16} style={{ marginRight: 4 }} /> Change
                    Recurring
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    disabled={rowSelectionModel.length === 0}
                    onClick={() => setOpenDialogType(DialogTypesT.REMOVE)}
                    aria-label="Remove Recurring"
                  >
                    <RiDeleteBinLine size={16} style={{ marginRight: 4 }} />{" "}
                    Remove Recurring
                  </Button>
                </Box>
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenDialogType(DialogTypesT.ADD)}
                    aria-label="Add Recurring"
                  >
                    <RiAddCircleLine size={16} style={{ marginRight: 4 }} /> Add
                    Recurring
                  </Button>
                </Box>
                {openDialogType === DialogTypesT.ADD && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <RecurringDialog
                      onClose={handleCloseChangeDialog}
                      onSuccessCallback={handleSuccessCallback}
                      selectedRows={[] as DeliveryItemT[]}
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.CHANGE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <RecurringDialog
                      derivedStartDate={nextDate}
                      derivedEndDate={lastDate}
                      onClose={handleCloseChangeDialog}
                      selectedRows={selectedRows as DeliveryItemT[]}
                      action={DialogTypesT.CHANGE}
                      onSuccessCallback={handleSuccessCallback}
                    />
                  </Dialog>
                )}
                {openDialogType === DialogTypesT.REMOVE && (
                  <Dialog
                    open={true}
                    onClose={handleCloseChangeDialog}
                    maxWidth="md"
                    fullWidth
                  >
                    <RecurringRemoveDialog
                      onClose={handleCloseChangeDialog}
                      selectedRows={selectedRows as DeliveryItemT[]}
                      action="REMOVE"
                      onSuccessCallback={handleSuccessCallback}
                    />
                  </Dialog>
                )}
              </Box>
            )}
            <GaneshDataGrid
              defaultGridState={defaultGridState}
              gridId="schedule-by-delivery-summary"
              loading={isLoading || isFetching}
              data={data || []}
              columns={columns}
              checkboxSelection={!isDatePast}
              disableRowSelectionOnClick
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={handleRowSelectionModelChange}
              keepNonExistentRowsSelected
            />
            <Snackbar
              open={snackbarOpen}
              autoHideDuration={3000}
              onClose={handleSnackbarClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
              <Alert
                onClose={handleSnackbarClose}
                severity="success"
                sx={{ width: "100%" }}
                icon={<RiCheckboxCircleLine size={24} />}
              >
                <Box>
                  <Typography variant="body1">{snackbarMessage}</Typography>
                </Box>
              </Alert>
            </Snackbar>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
